import React, { CSSProperties, forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import moment from 'moment'
import ReactPlayer from 'react-player/lazy'
import {
    Col, Row, Text, Icon, Image, Video,
    FileTile, AudioTile,
    FileProps, VideoProps, ImageProps, AudioProps,
    ProgressCircle, DeleteButtonOverlay,
    IconProps,
    useUI
} from './../../index'
import { MediaController } from '@evertel/web/media'
import { useService } from '@evertel/di'
import { APIDataMedia } from '@evertel/types'
import { NormalizedMedia } from '@evertel/media'
import { DownloadButton } from './DownloadButton'
import { HoverMedia } from '../HoverMedia'

export type ImageResizeMode = 'cover'| 'contain'| 'stretch'| 'repeat'| 'center'

type BaseMediaProps = {
    media: NormalizedMedia
    width: number
    height: number
    rounded?: boolean
    className?: string
    style?: CSSProperties
    allowDownload?: boolean
    onDelete?: (media: APIDataMedia | File) => void
    resizeMode?: ImageResizeMode
    memo?: boolean // memoize or not. default = false
    enableHover?: boolean
    enableContextMenu?: boolean

    // the following can be used to pass specific props to each media type.
    // any props here will override props above
    videoProps?: Omit<VideoProps, 'url'>
    imageProps?: Omit<ImageProps, 'url' | 'onDownload'>
    fileProps?: Omit<FileProps, 'url' | 'fileName' | 'onDownload'>
    audioProps?: Omit<AudioProps, 'url' | 'onDownload'>
}

type FileListMediaProps = BaseMediaProps & {
    variant: 'file-list'
    onClick: () => void
}

type OtherMediaProps = BaseMediaProps & {
    variant?: 'tile' | 'file-attachment' | 'preview'
    onClick?: never  // This makes onClick unavailable for other variants
}

type MediaProps = FileListMediaProps | OtherMediaProps

const Media = observer(forwardRef<HTMLImageElement & ReactPlayer & HTMLAudioElement, MediaProps>(({
    media,
    variant = 'tile',
    width,
    height,
    rounded,
    className,
    style = {},
    allowDownload,
    onClick,
    onDelete,
    resizeMode,
    memo,
    enableHover = false,
    enableContextMenu = false,
    videoProps,
    imageProps,
    fileProps,
    audioProps,
    ...otherProps
}, ref) => {

    const mediaController = useService(MediaController, [])

    const { addToast } = useUI()

    useState(() => {
        //must initialize in useState since it's synchronous so audioTile will be provided info on first render
        mediaController.init(media, width, height)
    })

    useEffect(() => {
        mediaController.updateDimensions(width, height)
    }, [width, height])

    if (!media) return null

    const handleContextMenu = (e: React.MouseEvent<HTMLElement>) => {
        if (!enableContextMenu) {
            e.preventDefault()
            e.stopPropagation()
        }
    }

    const handleDownloadMedia = async () => {
        try {
            await mediaController.downloadMedia(media)
        } catch (e) {
            addToast({
                title: 'Error',
                color: 'danger',
                message: e.message
            })
        }
    }

    const renderMediaElement = () => {
        switch (media.mediaType) {
            case 'image': {
                const imageElement = (
                    <Image
                        ref={ref}
                        url={mediaController?.url}
                        fileName={mediaController?.media.fileName}
                        width={width}
                        height={height}
                        {...(variant === 'tile' && { className })}
                        rounded={rounded}
                        resizeMode={resizeMode ?? 'contain'}
                        variant={variant}
                        enableContextMenu={enableContextMenu}
                        {...(enableContextMenu && {onDownload: () => handleDownloadMedia()})}
                        {...imageProps}
                        {...otherProps}
                    />
                )

                return (
                    enableHover ? (
                        <HoverMedia onDownload={() => handleDownloadMedia()}>
                            {imageElement}
                        </HoverMedia>
                    ) : imageElement
                )
            }

            case 'video': {
                const videoElement = (
                    <Video
                        ref={ref}
                        url={mediaController?.url}
                        fileName={mediaController?.media?.fileName}
                        width={width}
                        height={height}
                        className={className}
                        rounded={rounded}
                        thumbnailUrl={mediaController.videoThumbnailUrl}
                        duration={mediaController.videoDuration}
                        enableContextMenu={enableContextMenu}
                        {...(enableContextMenu && {onDownload: () => handleDownloadMedia()})}
                        {...videoProps}
                        {...otherProps}
                        variant={variant}
                    />
                )

                return enableHover ? (
                    <HoverMedia onDownload={() => handleDownloadMedia()}>
                        {videoElement}
                    </HoverMedia>
                ) : videoElement

            }

            case 'audio':
                if (variant === 'preview') {
                    return (
                        <FileTile
                            ref={ref}
                            url={mediaController?.url}
                            fileName={mediaController.media?.fileName}
                            fileSize={mediaController.media?.contentLength}
                            onDownload={() => handleDownloadMedia()}
                            variant={variant}
                            {...fileProps}
                            {...otherProps}
                        />
                    )
                } else {
                    const audioElement = (
                        <AudioTile
                            ref={ref}
                            url={mediaController?.url}
                            fileName={mediaController.media?.fileName}
                            fileSize={mediaController.humanizedFileSize}
                            variant={variant as any}
                            enableContextMenu={enableContextMenu}
                            {...(enableContextMenu && {onDownload: () => handleDownloadMedia()})}
                            {...audioProps}
                            {...otherProps}
                        />
                    )
                    return enableHover ? (
                        <HoverMedia className="audio-overlay" onDownload={() => handleDownloadMedia()}>
                            {audioElement}
                        </HoverMedia>
                    ) : audioElement
                }

            case 'file':
            case 'application':
            case 'text':
            default:
                return (
                    <FileTile
                        ref={ref}
                        url={mediaController?.url}
                        fileName={mediaController.media?.fileName}
                        fileSize={mediaController.media?.contentLength}
                        onDownload={() => handleDownloadMedia()}
                        variant={variant}
                        {...fileProps}
                        {...otherProps}
                    />
                )
        }
    }

    const combinedStyle = {
        width: 'fit-content',
        ...style // This will merge and overwrite as needed
    }

    if (['file-list', 'file-attachment'].includes(variant)) {
        return (
            <Row
                className={classNames('media-item',
                    `media-item-${media.mediaType}`,
                    `media-${variant}`,
                    {'m-1 p-1 pe-2 d-flex flex-grow-0 mw-100': (variant==='file-attachment') },
                    className)}
                style={combinedStyle}
            >
                <Col
                    // style={{ width: width }}
                    className="me-2 flex-grow-0"
                    {...(!enableContextMenu && { onContextMenu: handleContextMenu })}
                >
                    {React.createElement(
                        onClick ? 'button' : 'div',
                        {
                            className: 'p-0',
                            style: { display: 'block' },
                            ...(onClick && { onClick })
                        },
                        <div>
                            {['application', 'text', 'file', 'audio', 'video'].includes(media.mediaType) ? (
                                <div
                                    className="d-flex justify-content-center align-items-center rounded"
                                    style={{
                                        width: width,
                                        height: width,
                                        backgroundColor: mediaController.fileTypeAttributes.iconColor
                                    }}
                                >
                                    <Icon
                                        name={mediaController.fileTypeAttributes.iconName}
                                        color="white"
                                        size={width * 0.5}
                                    />
                                </div>
                            ) : (
                                renderMediaElement()
                            )}
                        </div>
                    )}
                </Col>
                <Col align="left" style={{minWidth: 0}}>
                    <Text
                        size="small"
                        bold
                        className="text-truncate"
                        title={mediaController.media.fileName}
                    >
                        {mediaController.media.fileName}
                    </Text>
                    <Text
                        size="small"
                    >
                        ({mediaController.humanizedFileSize})
                        {variant === 'file-list' && mediaController.media?.updatedDate && (
                            ` Shared on ${moment(mediaController.media?.updatedDate).format('l')}`
                        )}
                    </Text>
                </Col>

                {allowDownload &&
                    <DownloadButton onDownload={() => handleDownloadMedia()} />
                }
                {onDelete && (
                    <DeleteButtonOverlay
                        title={`Delete ${mediaController.media.fileName}`}
                        aria-label={`Delete ${mediaController.media.fileName}`}
                        onClick={() => onDelete(media)}
                    />
                )}
            </Row>
        )
    }

    return (
        <div
            className={classNames(
                'media-item',
                `media-item-${media.mediaType}`,
                className
            )}
            style={combinedStyle}
        >
            { media.state !== 'uploading' && renderMediaElement() }
            <div role="alert" aria-live="polite" aria-atomic="true">
                {media.state === 'uploading' && (
                    <div
                        style={{ width: width, height: height }}
                        className="media-loading mt-1"
                    >
                        <ProgressCircle
                            progress={media?.uploadProgress * 100}
                            radius={15}
                            stroke={3}
                        />
                        <span>
                            Uploading... {Math.floor(media?.uploadProgress * 100 / 5) * 5}%
                        </span>
                    </div>
                )}
            </div>
        </div>
    )

}))

export { Media }
